import apiClient from '../api.js';

// Capa de servicios para el alta y la edición de deudas, en la moneda que elige
// el operador. Backend: `UNICEN/modules/economico`.
//
// LA MONEDA SE DECIDE DEUDA POR DEUDA. No hay regla, catálogo de ítems
// dolarizados ni nada que consultar antes: quien carga la deuda elige si es en
// dólares o en bolivianos, y puede cambiarlo después al editarla.
//
// EL MONTO VIAJA SIEMPRE EN LA MONEDA ELEGIDA, tanto al crear como al editar.
// El backend lo convierte a bolivianos y guarda las dos cifras. El monto en
// bolivianos NO se manda cuando la moneda es dólares: es un valor derivado, y
// mandarlo sería pedirle al operador una cuenta que la base ya sabe hacer y que
// quedaría vieja al primer cambio de cotización.
//
//   GET    /economico/monedas
//          -> { data: [{ id_tipo_moneda, descripcion }] }
//          Solo 2 ($us) y 3 (Bs). El 1 ('%') es un porcentaje de beca.
//
//   GET    /economico/deudas/:id
//          -> { data: { id_deudas_contable, unicodigo, id_item_seguimiento,
//                       descripcion_item, id_gestion, id_plan_estudio,
//                       id_plan_economico, id_sede, id_tipo_moneda, es_dolar,
//                       monto_deuda, monto_descuento, monto_deuda_usd,
//                       monto_descuento_usd, id_tipo_cambio,
//                       tipo_cambio_factor, estado } }
//
//   POST   /economico/deudas
//          body: { unicodigo, id_sede, id_gestion, id_plan_estudio,
//                  id_plan_economico, id_item_seguimiento,
//                  monto, id_tipo_moneda, id_usuario,
//                  id_item_origen? }
//          -> 201 { data: <deuda creada, con las dos monedas> }
//
//          `id_item_origen` (opcional) es el concepto que paga una cuota
//          (COLEGIATURA, MATRÍCULA…). Debe ser un ítem activo que no sea otra
//          CUOTA{n}; si no, 404. La grilla lo muestra bajo el nombre de la cuota.
//
//          Reemplaza a POST /pagos/addnewitemdeuest SOLO para esta pantalla. El
//          endpoint legacy inserta con la moneda cableada en bolivianos y lo
//          siguen usando Recibos.vue, Anular.vue, Caja_congresos.vue y
//          Cambiopass.vue.
//
//   POST   /economico/deudas/cuotas
//          body: { unicodigo, id_sede, id_gestion, id_plan_estudio,
//                  id_plan_economico, cantidad_cuotas, monto_total,
//                  id_tipo_moneda, id_usuario, id_item_origen? }
//          -> 201 { data: { cantidad_cuotas, id_tipo_moneda, es_dolar,
//                           monto_total,
//                           cuotas: [{ numero, monto_asignado, ...deuda }] } }
//
//          Da de alta un PLAN DE CUOTAS: n deudas, una por cada ítem
//          CUOTA1..CUOTAn, en una sola transacción. El ítem "CUOTAS" que el
//          operador elige en la pantalla no existe en la base —es un asistente
//          de carga— y por eso este endpoint NO lleva `id_item_seguimiento`: los
//          ids los resuelve el backend contra el catálogo, porque el id no se
//          deriva del número de cuota (CUOTA13 es el id 24).
//
//          Se manda el TOTAL y la cantidad, nunca los montos de cada cuota: el
//          reparto lo hace el backend, en un solo lugar.
//
//   GET    /economico/deudas/pagado?unicodigo=&id_sede=&id_gestion=
//          -> { data: [{ id_item_seguimiento, pagado, pagos, deudas_con_el_item, ambiguo }] }
//
//          Lo PAGADO por ítem según las facturas vigentes (caja + historial, sin repetir el
//          mismo pago). Es la fuente de la columna PAGADO(Bs) de la pantalla de deuda: no se
//          deduce de total − descuento − saldo, porque ese saldo no siempre baja cuando se
//          registra una factura. `ambiguo` marca los ítems con más de una deuda del estudiante en
//          la gestión (dos carreras o un duplicado): ahí el pago no se atribuye a una sola.
//
//   PATCH  /economico/deudas/:id/monto
//          body: { monto, monto_descuento, id_tipo_moneda }
//          -> { data: <deuda actualizada> }
//
//          `id_tipo_moneda` es la moneda RESULTANTE, no la que tenía la deuda:
//          cambiar de moneda y cambiar de monto son la misma operación. Pasar
//          una deuda a bolivianos limpia las columnas de dólares; pasarla a
//          dólares sella la cotización vigente.
//
//   Errores de dominio esperables:
//     400 falta un identificador, la moneda no es 2 ni 3, o el monto es
//         negativo, con coma decimal o con más de 2 decimales.
//     404 la deuda no existe.
//     409 no hay ítems de cuota habilitados en el catálogo, o el estudiante ya
//         tiene cuotas cargadas en esa gestión (el mensaje dice cuáles).

const resource = '/economico/deudas';

const useDeudaApi = {
  listarMonedas() {
    return apiClient.get('/economico/monedas');
  },

  obtener(idDeuda) {
    return apiClient.get(`${resource}/${idDeuda}`);
  },

  crear(datos) {
    return apiClient.post(resource, datos);
  },

  crearPlanDeCuotas(datos) {
    return apiClient.post(`${resource}/cuotas`, datos);
  },

  listarPagado(params) {
    return apiClient.get(`${resource}/pagado`, { params });
  },

  actualizarMonto(idDeuda, datos) {
    return apiClient.patch(`${resource}/${idDeuda}/monto`, datos);
  },
};

export default useDeudaApi;
